import React, { useEffect, useRef, useState } from "react";
import { InputField } from "components/designV2/inputfield/inputfield";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { TextArea } from "components/designV2/textarea/textarea";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { Prompt } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  ErrorText,
  HiddenFileInput,
  LoadingCircle,
  LoadingSpinner,
  ProfileActions,
  ProfileAvatarImage,
  ProfileAvatarPlaceholder,
  ProfileContentGrid,
  ProfileFieldStack,
  ProfileForm,
  ProfileImageButton,
  ProfileImageButtonIcon,
  ProfileImagePanel,
  ProfileImageRow,
  ProfileImageSurface,
  ProfilePageTitle,
  ProfileSectionTitle,
  ProfileUploadButton,
} from "./profileSettings.styled";

export const ProfileSettings: React.FC = () => {
  const { t } = useTranslation();
  const [, , alertDispatch] = useAlertStore();
  const [companyState, companyActions] = useCompanyStore();
  const inputEl = useRef<HTMLInputElement>(null);
  const [companyName, setCompanyName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [description, setDescription] = useState("");
  const [imageError, setImageError] = useState(false);
  const [websiteError, setWebsiteError] = useState("");

  useEffect(() => {
    if (!companyState.company) {
      return;
    }

    setCompanyName(companyState.company.companyName || "");
    setDescription(companyState.company.description || "");
    setWebsiteUrl(companyState.company.websiteUrl || "");
  }, [companyState.company]);

  const validate = (): boolean => {
    return companyName.trim().length > 0;
  };

  const validateWebsite = (): boolean => {
    const trimmedWebsiteUrl = websiteUrl.trim();

    if (!trimmedWebsiteUrl) {
      setWebsiteError("");
      return true;
    }

    try {
      const normalizedUrl =
        trimmedWebsiteUrl.startsWith("http://") ||
        trimmedWebsiteUrl.startsWith("https://")
          ? trimmedWebsiteUrl
          : `https://${trimmedWebsiteUrl}`;

      const parsedUrl = new URL(normalizedUrl);
      if (parsedUrl.hostname) {
        setWebsiteError("");
        return true;
      }
    } catch (_error: unknown) {
      // Fall through to the validation message below.
    }

    setWebsiteError(t("ProfileCompany.PleaseEnterValidWebsiteUrl"));
    return false;
  };

  const madeChanges = (): boolean => {
    const company = companyState.company;
    if (!company) return false;

    return (
      companyName !== (company.companyName ?? "") ||
      websiteUrl !== (company.websiteUrl ?? "") ||
      description !== (company.description ?? "")
    );
  };

  const handleUpdate = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();

    if (!validate() || !validateWebsite() || !companyState.company) {
      return;
    }

    const trimmedCompanyName = companyName.trim();
    const trimmedWebsiteUrl = websiteUrl.trim();

    const data = {
      ...companyState.company,
      companyName: trimmedCompanyName,
      websiteUrl: trimmedWebsiteUrl,
      description,
    };

    companyActions
      .saveCompany(data, companyState.companies, companyState.company.id)
      .then(() => {
        alertDispatch({
          type: "SHOW_ALERT",
          payload: {
            icon: "checkmark",
            title: t("Alerts.Updated"),
            message: t("ProfileCompany.CompanyHasBeenUpdated"),
          },
        });
      })
      .catch(() => {
        alertDispatch({
          type: "SHOW_ALERT",
          payload: {
            icon: "alert",
            title: t("Alerts.OhNo"),
            message: t("ProfileCompany.CompanyCouldNotBeUpdated"),
          },
        });
      });
  };

  const updateImage = async (): Promise<void> => {
    const file = inputEl.current?.files?.[0];
    const companyId = companyState.company?.id;

    if (!file || !companyId) {
      return;
    }

    if (file.size >= 20971520) {
      setImageError(true);
      return;
    }

    try {
      setImageError(false);
      await companyActions.updateCompanyImage(companyId, file);

      if (companyState.company?.id) {
        companyActions.getCompanyImage(companyState.company.id);
      }
    } catch {
      alertDispatch({
        type: "SHOW_ALERT",
        payload: {
          icon: "alert",
          title: t("Alerts.OhNo"),
          message: t("General.ErrorOccurred"),
        },
      });
    }
  };

  return (
    <>
      <Prompt
        when={madeChanges()}
        message={t("ProfileCompany.YouHaveUnSavedChanges")}
      />
      <ProfileForm onSubmit={handleUpdate}>
        <ProfilePageTitle>{t("ProfileCompany.EditProfile")}</ProfilePageTitle>

        <ProfileContentGrid>
          <ProfileImagePanel>
            <ProfileSectionTitle>
              {t("ProfileWorker.ProfilePicture")}
            </ProfileSectionTitle>
            <ProfileImageRow>
              {companyState.imageIsLoading ? (
                <LoadingCircle>
                  <LoadingSpinner>
                    <CustomIcon
                      name="v2/reload"
                      size="36px"
                      color={ColorV2.Blue1}
                    />
                  </LoadingSpinner>
                </LoadingCircle>
              ) : companyState.companyImage ? (
                <ProfileImageSurface>
                  <ProfileAvatarImage
                    src={companyState.companyImage}
                    alt={t("ProfileWorker.ProfilePicture")}
                  />
                </ProfileImageSurface>
              ) : (
                <ProfileImageSurface>
                  <ProfileAvatarPlaceholder />
                </ProfileImageSurface>
              )}
              <HiddenFileInput
                hidden
                type="file"
                id="company-profile-upload"
                ref={inputEl}
                accept="image/png, image/jpeg"
                disabled={companyState.imageIsLoading}
                onChange={() => {
                  updateImage();
                }}
              />
              <ProfileImageButton
                type="button"
                aria-label={t("ProfileWorker.AddProfilePicture")}
                disabled={companyState.imageIsLoading}
                onClick={() => inputEl.current?.click()}
              >
                <ProfileImageButtonIcon />
              </ProfileImageButton>
            </ProfileImageRow>
            <ProfileUploadButton
              type="button"
              label={t("ProfileWorker.AddProfilePicture")}
              onClick={() => inputEl.current?.click()}
              disabled={companyState.imageIsLoading}
            />
            {imageError && (
              <ErrorText>{t("ProfileCompany.FileTooLarge")}</ErrorText>
            )}
          </ProfileImagePanel>

          <ProfileFieldStack>
            <InputField
              label={t("ProfileCompany.DisplayName")}
              type="text"
              value={companyName}
              onChange={setCompanyName}
              onClear={() => setCompanyName("")}
              fullWidth
            />
            <InputField
              label={t("ProfileCompany.Website")}
              type="text"
              placeholder="http://"
              value={websiteUrl}
              onChange={(value) => {
                setWebsiteUrl(value);
                if (websiteError) {
                  setWebsiteError("");
                }
              }}
              onBlur={() => {
                validateWebsite();
              }}
              onClear={() => {
                setWebsiteUrl("");
                setWebsiteError("");
              }}
              hasError={Boolean(websiteError)}
              errorText={websiteError}
              fullWidth
            />
            <TextArea
              label={t("ProfileCompany.Bio")}
              placeholder={t("RegisterCompany.TellUsAboutCompany")}
              value={description}
              onChange={setDescription}
              minHeight={180}
              maxHeight={300}
            />
          </ProfileFieldStack>
        </ProfileContentGrid>

        <ProfileActions>
          <PrimaryButton
            type="submit"
            label={t("General.Save")}
            disabled={!validate() || !madeChanges()}
          />
        </ProfileActions>
      </ProfileForm>
    </>
  );
};
